const UNIT_TO_CM = { cm: 1, in: 2.54, mm: 0.1 };

if (!customElements.get('foam-calculator')) {
  customElements.define(
    'foam-calculator',
    class FoamCalculator extends HTMLElement {
      constructor() {
        super();
        this.inputs = Array.from(this.querySelectorAll('[data-dimension]'));
        this.unitSelect = this.querySelector('[data-unit-select]');
        this.unitField = this.querySelector('.foam-calculator__unit-field');
        this.volumeField = this.querySelector('.foam-calculator__volume-field');
        this.unitsPerPieceField = this.querySelector('.foam-calculator__units-per-piece-field');
        this.volumeOutput = this.querySelector('[data-foam-volume]');
        this.priceOutput = this.querySelector('[data-foam-price]');
        this.unitVolume = parseFloat(this.dataset.unitVolume) || 2360;
        this.unitPrice = parseFloat(this.dataset.unitPrice) || 0;
        this.quantityInput = document.getElementById(this.dataset.quantityInput);
        this.currentUnit = this.unitSelect ? this.unitSelect.value : 'cm';

        try {
          this.formatter = new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
            style: 'currency',
            currency: this.dataset.currency || 'GBP',
          });
        } catch (error) {
          this.formatter = null;
        }

        this.recalculate = this.recalculate.bind(this);
        this.handleUnitChange = this.handleUnitChange.bind(this);
        this.handleVariantChange = this.handleVariantChange.bind(this);

        this.inputs.forEach((input) => input.addEventListener('input', this.recalculate));
        if (this.unitSelect) this.unitSelect.addEventListener('change', this.handleUnitChange);
      }

      connectedCallback() {
        this.applyUnitBounds();
        this.recalculate();

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribeVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, this.handleVariantChange);
        }
      }

      disconnectedCallback() {
        this.unsubscribeVariantChange?.();
      }

      handleVariantChange(event) {
        if (event?.data?.sectionId !== this.dataset.sectionId) return;
        const price = event?.data?.variant?.price;
        if (typeof price !== 'number') return;
        this.unitPrice = price;
        this.recalculate();
      }

      handleUnitChange() {
        const previousUnit = this.currentUnit;
        const nextUnit = this.unitSelect.value;
        const previousFactor = UNIT_TO_CM[previousUnit] || 1;
        const nextFactor = UNIT_TO_CM[nextUnit] || 1;

        this.inputs.forEach((input) => {
          const valueInCm = (parseFloat(input.value) || 0) * previousFactor;
          input.value = Math.round((valueInCm / nextFactor) * 100) / 100;
        });

        this.currentUnit = nextUnit;
        if (this.unitField) this.unitField.value = nextUnit;
        this.applyUnitBounds();
        this.recalculate();
      }

      applyUnitBounds() {
        const factor = UNIT_TO_CM[this.currentUnit] || 1;
        this.inputs.forEach((input) => {
          const minCm = parseFloat(input.dataset.minCm);
          const maxCm = parseFloat(input.dataset.maxCm);
          if (!Number.isNaN(minCm)) input.min = Math.round((minCm / factor) * 100) / 100;
          if (!Number.isNaN(maxCm)) input.max = Math.round((maxCm / factor) * 100) / 100;
        });
      }

      recalculate() {
        const factor = UNIT_TO_CM[this.currentUnit] || 1;
        const dimensionsCm = this.inputs.reduce((acc, input) => {
          const rawValue = Math.max(parseFloat(input.value) || 0, 0);
          acc[input.dataset.dimension] = rawValue * factor;
          return acc;
        }, {});

        const volumeCm3 = (dimensionsCm.length || 0) * (dimensionsCm.width || 0) * (dimensionsCm.thickness || 0);
        const volumeLitres = volumeCm3 / 1000;
        const units = volumeCm3 > 0 ? Math.max(1, Math.round(volumeCm3 / this.unitVolume)) : 0;
        const estimatedPriceCents = units * this.unitPrice;

        if (this.volumeOutput) this.volumeOutput.textContent = `${volumeLitres.toFixed(1)} L`;
        if (this.volumeField) this.volumeField.value = `${volumeLitres.toFixed(1)} L`;
        if (this.unitsPerPieceField) this.unitsPerPieceField.value = units;

        if (this.priceOutput) {
          this.priceOutput.textContent = this.formatter
            ? this.formatter.format(estimatedPriceCents / 100)
            : (estimatedPriceCents / 100).toFixed(2);
        }

        if (this.quantityInput) {
          this.quantityInput.value = units;
          this.quantityInput.step = units || 1;
          this.quantityInput.min = units || 1;
          this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }
  );
}
